import { Injectable } from '@angular/core'
import { Observable, Subject, interval, Subscription } from 'rxjs'
import { filter, retry, takeWhile } from 'rxjs/operators'
import { WebsocketAuthCommand } from './websocket-auth.command'
import { environment } from '@env/environment'
import { IWsMessage, WsMessageEventTypes } from './websocket.interface'
import { CurrentUser } from '../current-user'

@Injectable()
export class WebsocketService {
  private reconnectInterval = 3000
  private reconnectAttempts = 10
  private messages$ = new Subject<IWsMessage<any>>()
  private reconnectSub: Subscription | undefined
  private token: string
  private isOpened: boolean

  constructor(
    private websocketAuthCommand: WebsocketAuthCommand,
    private currentUser: CurrentUser
  ) {
    this.connect()
  }

  private connect(): void {
    if (!this.currentUser.id) {
      return
    }

    this.websocketAuthCommand
      .call()
      .pipe(retry({ count: this.reconnectAttempts, delay: this.reconnectInterval }))
      .subscribe((token) => {
        this.token = token

        if (!this.token) {
          return
        }

        const payload = JSON.stringify({ type: 'auth', token: this.token })
        const socket = new WebSocket(environment.websocket.connect)

        socket.onmessage = (event: MessageEvent) => {
          const data = JSON.parse(event.data) as IWsMessage<any>
          this.messages$.next(data)
        }

        let sendFailed = false

        socket.onopen = () => {
          this.isOpened = true
          try {
            socket.send(payload)
          } catch (e) {
            sendFailed = true
            fetch('/api/v1/app_errors/log', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                title: '[Frontend.WebSocketError] send failed in onopen',
                error_class: 'Frontend.WebSocketError',
                level: 'error',
                error_message: `readyState: ${socket.readyState}, error: ${e?.toString()}`,
                error_callstack: e instanceof Error ? e.stack : undefined,
                data: {
                  client: {
                    user_agent: navigator.userAgent,
                    current_url: window.location.href,
                    user_id: this.currentUser?.id,
                  },
                },
              }),
            }).catch(() => {})
            socket.close()
          }
        }

        socket.onerror = () => {
          socket.close()
        }

        socket.onclose = (event) => {
          if (event.code != 1000) {
            this.isOpened = false
            if (event.code !== 1006 && event.code !== 1001 && !sendFailed) {
              fetch('/api/v1/app_errors/log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  title: `[Frontend.WebSocketError] connection closed: ${event.code}`,
                  error_class: 'Frontend.WebSocketError',
                  level: 'error',
                  error_message: `code: ${event.code}, reason: ${event.reason || 'none'}, wasClean: ${event.wasClean}`,
                  data: {
                    client: {
                      user_agent: navigator.userAgent,
                      current_url: window.location.href,
                      user_id: this.currentUser?.id,
                    },
                  },
                }),
              }).catch(() => {})
            }
            this.reconnect()
          } else {
            this.isOpened = true
          }
        }
      })
  }

  private reconnect(): void {
    if (this.reconnectSub) {
      return
    }

    let attempts = 0
    let logged = false

    this.reconnectSub = interval(this.reconnectInterval)
      .pipe(
        takeWhile(() => {
          return !this.isOpened
        })
      )
      .subscribe({
        next: () => {
          attempts++
          if (attempts >= this.reconnectAttempts && !logged) {
            logged = true
            fetch('/api/v1/app_errors/log', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                title: '[Frontend.WebSocketError] reconnect not successful',
                error_class: 'Frontend.WebSocketError',
                level: 'error',
                error_message: `Still not connected after ${attempts} attempts (${this.reconnectInterval}ms interval), continuing to retry`,
                data: {
                  client: {
                    user_agent: navigator.userAgent,
                    current_url: window.location.href,
                    user_id: this.currentUser?.id,
                  },
                },
              }),
            }).catch(() => {})
          }
          this.connect()
        },
        error: () => {},
        complete: () => {
          this.reconnectSub?.unsubscribe()
          this.reconnectSub = undefined
        },
      })
  }

  public on(event?: WsMessageEventTypes): Observable<IWsMessage<any>> {
    return this.messages$.pipe(
      filter((message: IWsMessage<any>) => {
        if (!event) {
          return true
        }

        return message.metadata.event === event
      })
    )
  }
}
